import {useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import useAppDispatch from '../../hooks/useAppDispatch';
import {GlobalStyles} from '@mui/material';

import {
  hideCaptcha,
  selectBotDetection,
} from '@jobseeker/common-core/reducers/botDetection';
import {useBotCaptchaContext} from '@jobseeker/common-core/contexts/BotCaptchaContext';
import {TrackEventUBA} from '@jobseeker/common-core/utils/analytics';

import type {CaptchaProps} from '@ni-ui-libs/botguard-sdk/dist/components/react/captcha/types';
import type {ComponentType} from 'react';

const RECAPTCHA_SCRIPT_ID = 'recaptcha-script'; // must match SDK's internal id

const BotGuardCaptcha = () => {
  const dispatch = useAppDispatch();
  const {captchaShown, captchaSiteKey, captchaSource} =
    useSelector(selectBotDetection);
  const captchaSourceRef = useRef(captchaSource);
  captchaSourceRef.current = captchaSource;
  const {pendingRef} = useBotCaptchaContext();
  const [CaptchaComponent, setCaptchaComponent] =
    useState<ComponentType<CaptchaProps> | null>(null);
  const [grecaptchaReady, setGrecaptchaReady] = useState(false);

  useEffect(() => {
    // Load reCAPTCHA and the SDK bundle only when a 403 actually triggers the captcha,
    // and only once. Keeping this in _app.tsx means any page can trigger the captcha
    // without per-page wiring; the cost is a one-time ~200ms delay on first trigger.
    if (!captchaShown || CaptchaComponent) return;

    /* eslint-disable @typescript-eslint/no-explicit-any */
    const win = window as any;

    // SDK bug workaround: recaptchaWidgetId starts as undefined, making the SDK's
    // condition (id !== 0 && id !== undefined) evaluate to false and call reset()
    // instead of render() on first use. null passes both checks correctly.
    win.recaptchaWidgetId = null;

    const markReady = () =>
      win.grecaptcha.ready(() => setGrecaptchaReady(true));

    if (!document.getElementById(RECAPTCHA_SCRIPT_ID)) {
      const script = document.createElement('script');
      script.id = RECAPTCHA_SCRIPT_ID;
      script.src = 'https://www.google.com/recaptcha/api.js';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        console.log('[BotGuard] reCAPTCHA script loaded');
        markReady();
      };
      document.body.appendChild(script);
    } else if (win.grecaptcha?.ready) {
      markReady();
    }
    /* eslint-enable @typescript-eslint/no-explicit-any */

    Promise.all([
      import('@ni-ui-libs/botguard-sdk/dist/components/react'),
      import('@ni-ui-libs/botguard-sdk/dist/styles.css' as string),
    ]).then(([m]) => setCaptchaComponent(() => m.Captcha));
  }, [captchaShown, CaptchaComponent]);

  if (!CaptchaComponent) return null;

  return (
    <>
      <GlobalStyles
        styles={{'.bot-guard-captcha .wrap img': {display: 'none'}}}
      />
      <CaptchaComponent
        captcha={{
          // SDK bug workaround: only pass shown:true after grecaptcha is ready,
          // otherwise the render effect fires but grecaptcha.render is undefined
          // and the widget is silently skipped.
          shown: captchaShown && grecaptchaReady,
          siteKey: captchaSiteKey,
        }}
        callbacks={{
          successCallback: (token: string) => {
            pendingRef.current?.resolve(token);
            dispatch(hideCaptcha());
          },
          expiredCallback: () => {
            pendingRef.current?.reject(new Error('captcha expired'));
          },
          errorCallback: () => {
            pendingRef.current?.reject(new Error('captcha error'));
          },
          ubaCallback: ({payload, type}) => {
            const actionType = 'view';
            // console.log('[BotGuard] UBA event', data, {
            //   captchaSource: captchaSourceRef.current,
            // });
            TrackEventUBA('niBotGuardCaptchaEvent', actionType, {
              payload: JSON.stringify(payload),
              type,
              // captchaSource: captchaSourceRef.current,
            });
          },
        }}
      />
    </>
  );
};

export default BotGuardCaptcha;
